import { useMemo } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text, theme } from '@socrait/react';
import { marked } from 'marked';
import RenderHtml from 'react-native-render-html';

import { CopyButton } from '~/components/Accordion/CopyButton';
import { Loading } from '~/components/Loading';
import { useRenderHtmlConfig } from '~/hooks/useRenderHtmlConfig';

import { type IncludeKey, type MessageFormat, type Timeframe } from '~/services/studentData';
import { type IncludeOptions } from '~/store/studentData/studentDataStore';
import { ErrorBox } from '~/components/ErrorBox';

import { INCLUDE_LABELS, getTimeframeLabel } from './sharedFilters';

const FORMAT_LABELS: Record<MessageFormat, string> = {
  email: 'Email message',
  phone: 'Phone script',
  summary: 'Summary',
};

type MessagePanelProps = {
  included: IncludeOptions;
  timeframe: Timeframe;
  format: MessageFormat;
  message: string | undefined;
  isLoading: boolean;
  isError: boolean;
};

export function MessagePanel({ included, timeframe, format, message, isLoading, isError }: MessagePanelProps) {
  const includeLabel = (Object.keys(included) as IncludeKey[])
    .filter((k) => included[k])
    .map((k) => INCLUDE_LABELS[k])
    .join(' / ');
  const recapLabel = `${FORMAT_LABELS[format]} · ${includeLabel} · ${getTimeframeLabel(timeframe)}`;

  const { fonts, defaultTagStyles } = useRenderHtmlConfig();

  const messageHtml = useMemo(() => {
    if (message) {
      return marked.parse(message) as string;
    }
    return '';
  }, [message]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.recapText}>{recapLabel}</Text>
        <CopyButton text={{ plain: message ?? '', html: messageHtml }} />
      </View>

      <View style={styles.messageBox}>
        {isLoading && <Loading backgroundColor={theme.colors.lightGray} />}
        {isError && !isLoading && (
          <ErrorBox text="Failed to generate message. Please close this window and try again." />
        )}
        {!isLoading && !isError && (
          <ScrollView>
            <RenderHtml source={{ html: messageHtml }} tagsStyles={defaultTagStyles} systemFonts={fonts} />
          </ScrollView>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.xlarge,
    minWidth: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.xlarge,
  },
  recapText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
    flexShrink: 1,
  },
  messageBox: {
    flex: 1,
    borderRadius: theme.borderRadius.large,
    paddingVertical: theme.spacing.xlarge,
    paddingHorizontal: theme.spacing.xxlarge,
    backgroundColor: theme.colors.lightGray,
  },
});
